import {
  Button,
  FieldError,
  Group,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  NumberField,
  Popover,
  Select,
  SelectValue,
  Text,
  type NumberFieldProps,
} from "react-aria-components";
import { useAppSelector } from "../../../redux/hooks";
import { MARKETPLACE_CURRENCY_DENOMINATION } from "../../../utils/marketplaceConstants";
import { css, cx } from "../../../../styled-system/css";
import { inputClassNames } from "../styles/styles";
import { FaChevronDown } from "react-icons/fa6";
import { hstack } from "../../../../styled-system/patterns";
import { formatCurrency, formatPercent } from "../../../utils/formatNumber";
import type { Offset } from "../../../zodios/apiDefinitions/pricing";
import type { RecipeVariantProps } from "../../../../styled-system/types";

interface Props extends NumberFieldProps {
  label?: string;
  errorMessage?: string;
  description?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  className?: string;
  placeholder?: string;
  variant?: Exclude<RecipeVariantProps<typeof inputClassNames>, undefined>["variant"];
}

export const NumberInput = ({
  label,
  errorMessage,
  description,
  startIcon,
  endIcon,
  className,
  placeholder,
  variant,
  ...rest
}: Props) => {
  const classes = inputClassNames({ variant });
  return (
    <NumberField
      className={cx(classes.fieldWrapper, className)}
      formatOptions={{
        maximumFractionDigits: 2,
      }}
      isInvalid={!!errorMessage}
      {...rest}
    >
      {label && <Label className={classes.label}>{label}</Label>}
      <Group className={classes.inputBox}>
        {startIcon && (
          <span
            className={css({
              fontSize: "medium",
              fontWeight: "300",
            })}
          >
            {startIcon}
          </span>
        )}
        <Input className={cx(classes.input, css({ textAlign: "center" }))} placeholder={placeholder} />
        {endIcon && (
          <span
            className={css({
              fontSize: "medium",
              fontWeight: "300",
            })}
          >
            {endIcon}
          </span>
        )}
      </Group>
      {description && <Text slot="description">{description}</Text>}
      {errorMessage && <FieldError className={classes.error}>{errorMessage}</FieldError>}
    </NumberField>
  );
};

export const CurrencyInput = (props: Props) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const currencySymbol = MARKETPLACE_CURRENCY_DENOMINATION[marketPlace] ?? "$";
  return <NumberInput startIcon={currencySymbol} {...props} />;
};

export const CurrencyDisplay = ({ value }: { value: number }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  return (
    <span>
      {MARKETPLACE_CURRENCY_DENOMINATION[marketPlace] ?? "$"}
      {value}
    </span>
  );
};

type OffsetInputProps = Omit<Props, "value" | "onChange" | "defaultValue"> & {
  value: Offset;
  onChange: (value: Offset) => void;
  className?: string;
};

const listItemClasses = css({
  cursor: "pointer",
  minWidth: "30px",
  padding: "small",
  fontSize: "medium",
  textAlign: "center",
  _hover: {
    backgroundColor: "grey/10",
  },
  "&[data-selected='true']": {
    color: "white",
    backgroundColor: "trellisPalette.1",
    _hover: {
      backgroundColor: "trellisPalette.2",
    },
  },
});

export const OffsetInput = ({
  value,
  onChange,
  label,
  errorMessage,
  description,
  className,
  variant,
  ...rest
}: OffsetInputProps) => {
  const classes = inputClassNames({ variant });
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  return (
    <NumberField
      className={cx(classes.fieldWrapper, className)}
      value={value.amount}
      onChange={(amount) => onChange({ amount, type: value.type })}
      formatOptions={{
        maximumFractionDigits: 2,
      }}
      isInvalid={!!errorMessage}
      {...rest}
    >
      {label && <Label className={classes.label}>{label}</Label>}
      <Group className={classes.inputBox}>
        <Input className={cx(classes.input, css({ textAlign: "center" }))} />
        <Select
          className={hstack({
            cursor: "pointer",
            alignSelf: "stretch",
            borderLeft: "input",
            borderRightRadius: "input",
            marginRight: "-medium",
            _focusVisible: {
              outline: "2px solid {colors.fuchsia}",
            },
            "&[data-focused='true']": {
              border: "1px solid {colors.border.inputFocused}",
            },
          })}
          aria-label={"Offset type"}
          selectedKey={value.type}
          onSelectionChange={(type) =>
            onChange({
              amount: value.amount,
              type: type as "percent" | "currency",
            })
          }
        >
          <Button
            className={hstack({
              all: "unset",
              width: "100%",
              height: "100%",
              paddingLeft: "small",
              paddingRight: "medium",
              fontSize: "large",
            })}
          >
            <SelectValue />
            <span aria-hidden="true" className={css({ fontSize: "xxsmall" })}>
              <FaChevronDown />
            </span>
          </Button>
          <Popover offset={1}>
            <ListBox
              className={css({
                display: "flex",
                flexDirection: "column",
                border: "1px solid {colors.grey}",
                outline: "none",
                borderBottomRadius: "standard",
                width: "calc(var(--trigger-width) - {spacing.medium})",
                paddingBottom: "small",
                backgroundColor: "white",
              })}
            >
              <ListBoxItem id={"percent"} className={listItemClasses}>
                %
              </ListBoxItem>
              <ListBoxItem id={"currency"} className={listItemClasses}>
                {MARKETPLACE_CURRENCY_DENOMINATION[marketPlace] ?? "$"}
              </ListBoxItem>
            </ListBox>
          </Popover>
        </Select>
      </Group>
      {description && <Text slot="description">{description}</Text>}
      {errorMessage && <FieldError className={classes.error}>{errorMessage}</FieldError>}
    </NumberField>
  );
};

export const OffsetDisplay = ({ value }: { value: Offset }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const displayValue =
    value.type === "percent" ?
      formatPercent(value.amount, 2, true)
    : formatCurrency(value.amount, marketPlace, false, true);
  return <span>{displayValue}</span>;
};
